import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';

import { Input } from '@/components/form/Input';
import { MultiValueInput } from '@/components/form/MultiValueInput';
import { ProfilePictureUpload } from '@/components/ProfilePictureUpload';
import { Tooltip } from '@/components/Tooltip';
import { SubtenantConfirmationDialog } from '@/sections/components/SubtenantConfirmationDialog';
import { StepProps } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import { CustomerTypeChangeConfirmationDialog } from '@/sections/customerManagement/components/modals/AddCustomerModal/components';
import { CustomerTypeOptions } from '@/sections/customerManagement/components/modals/UpdateCustomerTypeModal';
import {
  BUG_BOUNTY_SUBSCRIPTION_CONFIG,
  DEFAULT_FORM_DATA,
  getDefaultsForType,
  MANAGED_SUBSCRIPTION_CONFIG,
  SAAS_SUBSCRIPTION_CONFIG,
} from '@/sections/customerManagement/constants';
import { useCustomerDisplayName } from '@/sections/customerManagement/hooks/useCustomerDisplayName';
import { useAuth } from '@/state/auth';
import { CustomerTypeValue } from '@/types';

/**
 * Step 1: Basic Information - Organization name, profile picture, and subscription type
 */
export const BasicInformationStep: React.FC<StepProps> = ({
  formData,
  setFormData,
  isSubmitting,
}) => {
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    newType: CustomerTypeValue | null;
  }>({
    isOpen: false,
    newType: null,
  });
  const [parentSearch, setParentSearch] = useState('');
  const [parentDropdownOpen, setParentDropdownOpen] = useState(false);
  const [pendingParent, setPendingParent] = useState<{
    email: string;
    displayName: string;
  } | null>(null);

  // Use the accounts list from whoami (includes subtenant-inherited accounts)
  const { whoami } = useAuth();
  const { dataMap: displayNameMap } = useCustomerDisplayName();

  const parentTenantOptions = useMemo(() => {
    const accountNames = whoami?.accounts ?? [];
    return accountNames
      .map(email => ({
        email,
        displayName: displayNameMap.get(email) || email,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [whoami?.accounts, displayNameMap]);

  const filteredParentOptions = useMemo(() => {
    if (!parentSearch) return parentTenantOptions;
    const q = parentSearch.toLowerCase();
    return parentTenantOptions.filter(t =>
      t.displayName.toLowerCase().includes(q)
    );
  }, [parentTenantOptions, parentSearch]);

  const hasUserSetValues = (): boolean => {
    // Get type-aware defaults for CURRENT customer type
    // This accounts for auto-initialized values (e.g., subscription_assets = 0 for MANAGED)
    const typeDefaults = getDefaultsForType(formData.customer_type);

    // Compare current formData against type-aware defaults
    // Only returns true if user has manually modified values beyond auto-initialization
    return (
      // Attack surface selections (Step 2)
      JSON.stringify(formData.attack_surface) !==
        JSON.stringify(typeDefaults.attack_surface) ||
      // Period of performance dates (Step 3)
      formData.subscription_start_date !==
        typeDefaults.subscription_start_date ||
      formData.subscription_end_date !== typeDefaults.subscription_end_date ||
      // Subscription pricing (Step 4) - compared against type-aware defaults
      formData.subscription_cost !== typeDefaults.subscription_cost ||
      formData.subscription_msrp !== typeDefaults.subscription_msrp ||
      formData.subscription_assets !== typeDefaults.subscription_assets ||
      formData.subscription_applications !==
        typeDefaults.subscription_applications ||
      formData.subscription_tier !== typeDefaults.subscription_tier ||
      formData.subscription_scan_schedule !==
        typeDefaults.subscription_scan_schedule ||
      // PlexTrac configuration (Step 3)
      formData.plextrac_id !== typeDefaults.plextrac_id ||
      // Team members (Steps 5-6)
      formData.collaborators.length !== typeDefaults.collaborators.length
    );
  };

  const handleCustomerTypeChange = (newType: CustomerTypeValue | null) => {
    // If null is selected, ignore (shouldn't happen in practice)
    if (!newType) return;

    // If this is the initial selection (no current type), apply directly
    if (!formData.customer_type) {
      applyCustomerTypeChange(newType);
      return;
    }

    // If changing type and user has set values, show confirmation dialog
    if (formData.customer_type !== newType) {
      if (hasUserSetValues()) {
        setConfirmationDialog({
          isOpen: true,
          newType,
        });
      } else {
        // No user-set values, apply change directly
        applyCustomerTypeChange(newType);
      }
    }
  };

  const applyCustomerTypeChange = (newType: CustomerTypeValue) => {
    // Reset to default form data but preserve basic info
    const resetFormData = {
      ...DEFAULT_FORM_DATA,
      // Preserve basic information
      settings_display_name: formData.settings_display_name,
      profile_picture: formData.profile_picture,
      allowed_domains: formData.allowed_domains,
      parent_account: formData.parent_account,
      // Set new customer type
      customer_type: newType,
    };

    // Apply customer type-specific defaults
    if (newType === 'FREEMIUM') {
      resetFormData.attack_surface = DEFAULT_FORM_DATA.attack_surface;
    }

    // Initialize subscription fields for MANAGED/SAAS/BUG_BOUNTY customer types
    if (
      newType === 'MANAGED' ||
      newType === 'SAAS' ||
      newType === 'BUG_BOUNTY'
    ) {
      const config =
        newType === 'MANAGED'
          ? MANAGED_SUBSCRIPTION_CONFIG
          : newType === 'SAAS'
            ? SAAS_SUBSCRIPTION_CONFIG
            : BUG_BOUNTY_SUBSCRIPTION_CONFIG;
      resetFormData.subscription_tier = config.selectedTier;
      resetFormData.subscription_scan_schedule = config.scanSchedule;
      resetFormData.subscription_assets = 0;
      resetFormData.subscription_applications = 0;
    }

    setFormData(resetFormData);
  };

  const handleConfirmTypeChange = () => {
    if (confirmationDialog.newType) {
      applyCustomerTypeChange(confirmationDialog.newType);
    }
    setConfirmationDialog({ isOpen: false, newType: null });
  };

  const handleCancelTypeChange = () => {
    setConfirmationDialog({ isOpen: false, newType: null });
  };
  return (
    <div className="flex h-[700px] flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="mb-8">
          <div className="flex gap-6">
            <div className="flex flex-col gap-2">
              <div className="mb-2">
                <div className="flex items-center gap-2">
                  <label className="text-chariot-text-primary block text-sm font-medium">
                    Customer Logo{' '}
                    <span className="text-chariot-stroke-critical">*</span>
                  </label>
                  <Tooltip title="Upload your customer's logo (required)">
                    <QuestionMarkCircleIcon className="text-chariot-text-secondary hover:text-chariot-text-primary h-4 w-4 cursor-help" />
                  </Tooltip>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <ProfilePictureUpload
                  source={
                    formData.profile_picture
                      ? { type: 'buffer', buffer: formData.profile_picture }
                      : { type: 'empty' }
                  }
                  onUpload={buffer =>
                    setFormData(prev => ({ ...prev, profile_picture: buffer }))
                  }
                  onDelete={() =>
                    setFormData(prev => ({
                      ...prev,
                      profile_picture: undefined,
                    }))
                  }
                  size="sm"
                  shape="square"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Vertical divider */}
            <div className="border-chariot-stroke w-px self-stretch border-l" />

            <div className="flex flex-1 flex-col gap-4">
              <div className="relative">
                <Input
                  label={
                    <>
                      Customer Name{' '}
                      <span className="text-chariot-stroke-critical">*</span>
                    </>
                  }
                  name="settings_display_name"
                  value={formData.settings_display_name}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      settings_display_name: e.target.value,
                    }))
                  }
                  placeholder="Enter customer name"
                  disabled={isSubmitting}
                />
              </div>

              <div className="relative">
                <label className="text-chariot-text-primary mb-1 block text-sm font-medium">
                  Parent Account{' '}
                  <span className="text-chariot-text-secondary text-xs font-normal">
                    (optional)
                  </span>
                </label>
                {formData.parent_account ? (
                  <div className="border-chariot-stroke bg-chariot-background-secondary flex items-center justify-between rounded-lg border px-3 py-2">
                    <div>
                      <span className="text-chariot-text-primary text-sm">
                        {displayNameMap.get(formData.parent_account) ||
                          formData.parent_account}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-chariot-text-secondary hover:text-chariot-text-primary"
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          parent_account: undefined,
                        }))
                      }
                      disabled={isSubmitting}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Input
                      name="parent_search"
                      value={parentSearch}
                      onChange={e => {
                        setParentSearch(e.target.value);
                        setParentDropdownOpen(true);
                      }}
                      onFocus={() => setParentDropdownOpen(true)}
                      onBlur={() => {
                        setTimeout(() => setParentDropdownOpen(false), 200);
                      }}
                      placeholder="Search for a parent account..."
                      disabled={isSubmitting}
                    />
                    {parentDropdownOpen && filteredParentOptions.length > 0 && (
                      <div className="border-chariot-stroke absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border bg-white shadow-lg dark:bg-zinc-900">
                        {filteredParentOptions.map(option => (
                          <button
                            key={option.email}
                            type="button"
                            className="hover:bg-chariot-background-secondary w-full px-3 py-2 text-left"
                            onMouseDown={e => {
                              e.preventDefault();
                              setPendingParent(option);
                              setParentSearch('');
                              setParentDropdownOpen(false);
                            }}
                          >
                            <span className="text-chariot-text-primary text-sm">
                              {option.displayName}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="relative">
                <label className="text-chariot-text-primary mb-1 block text-sm font-medium">
                  Allowed Email Domains{' '}
                  <span className="text-chariot-stroke-critical">*</span>
                </label>
                <p className="text-chariot-text-secondary mb-2 text-xs">
                  Only collaborators with these email domains can be added to
                  this account.
                </p>
                <MultiValueInput
                  values={formData.allowed_domains}
                  onChange={domains =>
                    setFormData(prev => ({
                      ...prev,
                      allowed_domains: domains.map(d => d.trim().toLowerCase()),
                    }))
                  }
                  editable={!isSubmitting}
                  placeholder="Enter a domain (e.g. acme.com)"
                  aria-label="Allowed email domains"
                  addButtonLabel="Add Domain"
                />
                {formData.allowed_domains.length === 0 && (
                  <p className="text-chariot-text-secondary mt-1 text-xs italic">
                    Any valid email domain can be added. Add at least one to
                    continue.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="mb-6 flex items-center gap-2">
            <h3 className="text-chariot-text-primary text-base font-medium">
              Select Subscription Type{' '}
              <span className="text-chariot-stroke-critical">*</span>
            </h3>
            <Tooltip title="Choose the type of subscription that best fits this customer's needs">
              <QuestionMarkCircleIcon className="text-chariot-text-secondary hover:text-chariot-text-primary h-4 w-4 cursor-help" />
            </Tooltip>
          </div>
          <div className="space-y-3">
            <CustomerTypeOptions
              selectedType={formData.customer_type}
              setSelectedType={handleCustomerTypeChange}
              isPending={isSubmitting}
            />
          </div>
        </div>
      </div>

      <CustomerTypeChangeConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onConfirm={handleConfirmTypeChange}
        onCancel={handleCancelTypeChange}
        currentType={formData.customer_type}
        newType={confirmationDialog.newType!}
      />
      <SubtenantConfirmationDialog
        isOpen={!!pendingParent}
        onConfirm={() => {
          if (pendingParent) {
            setFormData(prev => ({
              ...prev,
              parent_account: pendingParent.email,
            }));
          }
          setPendingParent(null);
        }}
        onCancel={() => setPendingParent(null)}
        parentDisplayName={pendingParent?.displayName ?? ''}
        childDisplayName={formData.settings_display_name || 'the new customer'}
      />
    </div>
  );
};
